import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { assertResolvedAtsNotHttpError } from '@/api/ats/atsEnvelope';
import { http } from '@/lib/ats-https';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InternalEmailUser {
  id: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  avatar_url?: string;
}

export interface InternalEmailOrgRole {
  id: number;
  name: string;
}

/** One recipient row as the prefill returns it. */
export interface InternalEmailRecipientSeed {
  users?: InternalEmailUser[];
  participant_groups?: string[];
  org_roles?: InternalEmailOrgRole[];
}

/** A candidate document the sender may attach — names and ids only. */
export interface InternalEmailDocument {
  blob_id?: number;
  ats_attachment_id?: number;
  filename?: string;
  content_type?: string;
  byte_size?: number;
}

export interface InternalEmailDocuments {
  offer_documents?: InternalEmailDocument[];
  resume?: InternalEmailDocument | null;
  scorecards_available?: boolean;
  other_files?: InternalEmailDocument[];
}

export interface InternalEmailPrefill {
  sender?: InternalEmailUser | null;
  ats_shared_email?: { id: number; email?: string; name?: string } | null;
  to?: InternalEmailRecipientSeed;
  cc?: InternalEmailRecipientSeed;
  bcc?: InternalEmailRecipientSeed;
  subject?: string | null;
  body?: string | null;
  template_attachments?: InternalEmailDocument[];
  documents?: InternalEmailDocuments;
  template_category?: string;
}

export interface InternalEmailMessage {
  id: number;
  stage?: string;
  sent_at?: string | null;
  user?: InternalEmailUser;
}

/** One attachment as the show payload returns it — no URL, names and sizes only. */
export interface InternalEmailAttachment {
  id: number;
  filename?: string;
  content_type?: string;
  byte_size?: number;
}

export interface InternalEmail {
  id: number;
  stage?: string;
  status?: string;
  scheduled_at?: string | null;
  sent_at?: string | null;
  timezone?: string | null;
  subject?: string;
  body?: string;
  /** Who it went out as. Only the show payload carries these. */
  sender?: InternalEmailUser | null;
  ats_shared_email?: { id: number; email?: string; name?: string } | null;
  attachments?: InternalEmailAttachment[];
  to?: InternalEmailUser[];
  cc?: InternalEmailUser[];
  bcc?: InternalEmailUser[];
  messages?: InternalEmailMessage[];
}

/** What the send form submits. Roles resolve to people server-side, at send time. */
export interface SendInternalEmailPayload {
  ats_application_id: number | string;
  /** Absent on an ad-hoc send, which has no activity behind it. */
  ats_job_stage_action_id?: number | string;
  sender_id?: number | string | null;
  ats_shared_email_id?: number | string | null;
  to_user_ids?: (string | number)[];
  to_participant_groups?: string[];
  to_org_role_ids?: number[];
  cc_user_ids?: (string | number)[];
  cc_participant_groups?: string[];
  cc_org_role_ids?: number[];
  bcc_user_ids?: (string | number)[];
  bcc_participant_groups?: string[];
  bcc_org_role_ids?: number[];
  subject?: string;
  body?: string;
  scheduled_at?: string | null;
  timezone?: string | null;
  /** Existing candidate documents, attached by reference. */
  ats_attachment_ids?: number[];
  offer_document_blob_ids?: number[];
  include_resume?: boolean;
  include_scorecards?: boolean;
  /** Newly uploaded files, which are the only ones carrying bytes. */
  attachments?: File[];
}

// ---------------------------------------------------------------------------
// Payload
// ---------------------------------------------------------------------------

const appendList = (
  formData: FormData,
  key: string,
  values?: (string | number)[]
) => {
  if (!values) return;
  // Multipart drops a key with no values, and the API reads a missing list as
  // "fall back to the activity" rather than "the sender cleared it" — so an
  // empty list still has to put something on the wire. The API strips the
  // marker back out; sending it alongside real values would fail :integer.
  if (values.length === 0) {
    formData.append(`${key}[]`, '');
    return;
  }
  values.forEach((value) => formData.append(`${key}[]`, String(value)));
};

export const buildSendInternalEmailFormData = (
  payload: SendInternalEmailPayload
): FormData => {
  const formData = new FormData();

  formData.append('ats_application_id', String(payload.ats_application_id));
  // Omitted entirely on an ad-hoc send — sending "undefined" would be read as
  // an activity id and 404.
  if (payload.ats_job_stage_action_id) {
    formData.append(
      'ats_job_stage_action_id',
      String(payload.ats_job_stage_action_id)
    );
  }

  if (payload.sender_id)
    formData.append('sender_id', String(payload.sender_id));
  if (payload.ats_shared_email_id) {
    formData.append('ats_shared_email_id', String(payload.ats_shared_email_id));
  }

  (['to', 'cc', 'bcc'] as const).forEach((prefix) => {
    appendList(formData, `${prefix}_user_ids`, payload[`${prefix}_user_ids`]);
    appendList(
      formData,
      `${prefix}_participant_groups`,
      payload[`${prefix}_participant_groups`]
    );
    appendList(
      formData,
      `${prefix}_org_role_ids`,
      payload[`${prefix}_org_role_ids`]
    );
  });

  if (payload.subject !== undefined) {
    formData.append('subject', payload.subject ?? '');
  }
  if (payload.body !== undefined) formData.append('body', payload.body ?? '');
  if (payload.scheduled_at) {
    formData.append('scheduled_at', payload.scheduled_at);
  }
  if (payload.timezone) formData.append('timezone', payload.timezone);

  appendList(formData, 'ats_attachment_ids', payload.ats_attachment_ids);
  appendList(
    formData,
    'offer_document_blob_ids',
    payload.offer_document_blob_ids
  );
  if (payload.include_resume !== undefined) {
    formData.append('include_resume', String(Boolean(payload.include_resume)));
  }
  if (payload.include_scorecards !== undefined) {
    formData.append(
      'include_scorecards',
      String(Boolean(payload.include_scorecards))
    );
  }

  payload.attachments?.forEach((file) =>
    formData.append('attachments[]', file)
  );

  return formData;
};

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

export async function fetchInternalEmailPrefill(params: {
  applicationId: number | string;
  activityId?: number | string;
}): Promise<InternalEmailPrefill> {
  const query = new URLSearchParams({
    ats_application_id: String(params.applicationId),
  });
  // An ad-hoc send has no activity; the key is left off rather than sent empty.
  if (params.activityId) {
    query.set('ats_job_stage_action_id', String(params.activityId));
  }

  const res = (await http.get(`/internal_emails/new?${query.toString()}`)) as {
    data?: InternalEmailPrefill;
  };
  assertResolvedAtsNotHttpError(res);
  return res?.data ?? {};
}

export async function sendInternalEmail(
  payload: SendInternalEmailPayload
): Promise<InternalEmail> {
  const res = (await http.post(
    '/internal_emails',
    buildSendInternalEmailFormData(payload)
  )) as { data?: InternalEmail };
  assertResolvedAtsNotHttpError(res);
  return (res?.data ?? {}) as InternalEmail;
}

export async function fetchInternalEmail(
  id: number | string
): Promise<InternalEmail> {
  const res = (await http.get(
    `/internal_emails/${encodeURIComponent(String(id))}`
  )) as { data?: InternalEmail };
  assertResolvedAtsNotHttpError(res);
  return (res?.data ?? {}) as InternalEmail;
}

export async function cancelInternalEmail(
  id: number | string
): Promise<InternalEmail> {
  const res = (await http.post(`/internal_emails/${id}/cancel`)) as {
    data?: InternalEmail;
  };
  assertResolvedAtsNotHttpError(res);
  return (res?.data ?? {}) as InternalEmail;
}

export async function updateInternalEmail({
  id,
  payload,
}: {
  id: number | string;
  payload: SendInternalEmailPayload;
}): Promise<InternalEmail> {
  const res = (await http.patch(
    `/internal_emails/${encodeURIComponent(String(id))}`,
    buildSendInternalEmailFormData(payload)
  )) as { data?: InternalEmail };
  assertResolvedAtsNotHttpError(res);
  return (res?.data ?? {}) as InternalEmail;
}

export async function sendInternalEmailNow(
  id: number | string
): Promise<InternalEmail> {
  const res = (await http.post(`/internal_emails/${id}/send_now`)) as {
    data?: InternalEmail;
  };
  assertResolvedAtsNotHttpError(res);
  return (res?.data ?? {}) as InternalEmail;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const internalEmailQueryKeys = {
  all: ['internal-emails'] as const,
  prefill: (applicationId: number | string, activityId: number | string) =>
    [
      ...internalEmailQueryKeys.all,
      'prefill',
      applicationId,
      activityId,
    ] as const,
  detail: (id: number | string) =>
    [...internalEmailQueryKeys.all, 'detail', id] as const,
};

/**
 * Seeds the compose slider from the activity's configuration.
 *
 * Not cached between openings: the roles configured on the activity resolve to
 * whoever holds them right now, and the candidate's documents change as the
 * team works the application.
 */
export function useInternalEmailPrefill(params: {
  applicationId?: number | string;
  activityId?: number | string;
  enabled?: boolean;
}): UseQueryResult<InternalEmailPrefill, Error> {
  const { applicationId, activityId, enabled = true } = params;

  return useQuery({
    queryKey: internalEmailQueryKeys.prefill(
      applicationId ?? '',
      activityId ?? ''
    ),
    queryFn: () =>
      fetchInternalEmailPrefill({
        applicationId: applicationId as number | string,
        activityId: activityId as number | string,
      }),
    enabled: Boolean(enabled && applicationId),
    staleTime: 0,
  });
}

/**
 * One sent internal email, for the read-only viewer.
 *
 * Unlike the prefill this is a record of what went out, so it is safe to cache:
 * the copy is fixed once sent. Per-recipient delivery can still move while
 * copies are in flight, which is what `staleTime` keeps short.
 */
export function useInternalEmail(
  id?: number | string,
  options: { enabled?: boolean } = {}
): UseQueryResult<InternalEmail, Error> {
  const { enabled = true } = options;

  return useQuery({
    queryKey: internalEmailQueryKeys.detail(id ?? ''),
    queryFn: () => fetchInternalEmail(id as number | string),
    enabled: Boolean(enabled && id),
    staleTime: 30_000,
  });
}

export function useSendInternalEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendInternalEmail,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: internalEmailQueryKeys.all }),
  });
}

export function useUpdateInternalEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateInternalEmail,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: internalEmailQueryKeys.all }),
  });
}

export function useSendInternalEmailNow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendInternalEmailNow,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: internalEmailQueryKeys.all }),
  });
}

export function useCancelInternalEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelInternalEmail,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: internalEmailQueryKeys.all }),
  });
}
